<template>
  <v-form v-model="isFormValid" class="aveine-quiz-form">
    <v-container v-if="dataLoaded">

      <!-- Le jour ou le quiz sort (REDMINE-13588) : un quiz par jour, et pas de
           quiz un jour sans quiz planifie. Obligatoire. -->
      <v-menu v-model="dayMenu" :close-on-content-click="false" max-width="290">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formatDay(day)"
            :label="$t('intranet.homeContent.quiz.day')"
            :hint="$t('intranet.homeContent.quiz.dayHint')"
            persistent-hint
            :rules="[() => !!day || $t('intranet.homeContent.validation.dayRequired')]"
            :error-messages="dayTaken ? [dayTakenMessage] : []"
            :loading="checkingDay"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker v-model="day" :locale="$i18n.locale" first-day-of-week="1" @input="dayMenu = false"></v-date-picker>
      </v-menu>

      <v-alert v-if="dayTaken" type="error" dense text class="mt-2">
        {{ dayTakenMessage }}
        <router-link
          v-if="conflict"
          :to="`/authenticated/quizzes/update/${conflict.id}`"
        >{{ $t('intranet.homeContent.quiz.openExisting') }}</router-link>
      </v-alert>

      <AveineTranslatedField :value="quiz.theme" :label="$t('intranet.homeContent.quiz.theme')" />
      <AveineTranslatedField :value="quiz.question" :label="$t('intranet.homeContent.quiz.question')" />
      <!-- Explication facultative : aucune langue exigee, d'ou le tableau vide.
           Une section nommee « Le saviez-vous » gagne a porter le pourquoi,
           mais on ne bloque pas l'enregistrement dessus. -->
      <AveineTranslatedField
        :value="quiz.explanation"
        :label="$t('intranet.homeContent.quiz.explanation')"
        :required="[]"
      />

      <v-divider class="my-9"></v-divider>

      <div class="d-flex align-center mb-4">
        <h3>{{ $t('intranet.homeContent.quiz.answers') }}</h3>
        <v-spacer></v-spacer>
        <v-btn depressed color="primary" @click="addAnswer()">
          {{ $t('intranet.homeContent.button.addAnswer') }}
        </v-btn>
      </div>

      <!-- Un v-radio-group et non des cases a cocher : la base n'accepte qu'une
           seule bonne reponse par quizz (index unique partiel), et la maquette
           montre une selection unique suivie d'un feedback immediat. -->
      <v-radio-group v-model="correctIndex">
        <v-row
          v-for="(answer, index) in answers"
          :key="answer.$id || answer.id || index"
          class="align-center"
        >
          <v-col cols="1" class="d-flex justify-center">
            <v-radio :value="index" :aria-label="$t('intranet.homeContent.quiz.correct')"></v-radio>
          </v-col>
          <v-col>
            <AveineTranslatedField
              :value="answer.label"
              :label="$t('intranet.homeContent.quiz.answerLabel', { position: index + 1 })"
            />
          </v-col>
          <v-col cols="1" class="d-flex justify-center">
            <v-btn icon color="error" @click="removeAnswer(index)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-radio-group>

      <v-alert v-if="answersError" type="warning" dense text>{{ answersError }}</v-alert>

      <v-divider class="my-9"></v-divider>

      <v-switch
        v-model="quiz.published"
        :label="$t('intranet.homeContent.attributes.published')"
        :hint="$t('intranet.homeContent.quiz.publishedHint')"
        persistent-hint
      ></v-switch>

      <div class="d-flex justify-center mt-9">
        <v-btn
          class="md-raised"
          :disabled="!isFormValid || !!answersError || !day || dayTaken || checkingDay"
          depressed
          color="primary"
          @click="submit()"
        >
          {{ $t('intranet.referential.button.submit') }}
        </v-btn>
      </div>
    </v-container>

    <div v-else class="d-flex justify-center align-center mt-16">
      <v-progress-circular indeterminate :size="100" :width="10" color="secondary"></v-progress-circular>
    </div>
  </v-form>
</template>

<script>
import { errorHandler } from 'aveine-toolbox'
import AveineTranslatedField from '@/components/AveineTranslatedField.vue'
import Quiz from '@/store/models/quiz'
import QuizAnswer from '@/store/models/quiz-answer'

/**
 * Formulaire d'un quizz « Le saviez-vous » (REDMINE-12962).
 *
 * Les reponses sont enregistrees APRES le quizz, dans des requetes distinctes :
 * `quiz_answers.quiz_id` est NOT NULL, elles ne peuvent donc pas exister avant
 * lui. La creation n'est pas atomique, et c'est assume — un echec en cours de
 * route laisse un quizz incomplet, mais non publie, donc invisible de
 * l'application.
 *
 * Repartition des garanties :
 *   - la base garantit qu'un quizz ne porte jamais DEUX bonnes reponses ;
 *   - ce formulaire garantit qu'il en porte AU MOINS une avant publication.
 */
export default {
  name: 'AveineQuizForm',
  components: { AveineTranslatedField },
  props: {
    quiz: { type: Object, required: true },
    APIbaseURL: String
  },
  data: () => ({
    isFormValid: false,
    dataLoaded: false,
    dayMenu: false,
    // « 2026-10-05 », la forme de v-date-picker.
    day: null,
    // Le quiz deja planifie ce jour-la, s'il y en a un et qu'il n'est pas celui
    // qu'on edite.
    conflict: null,
    checkingDay: false,
    answers: [],
    correctIndex: null,
    // Reponses presentes a l'ouverture et retirees depuis : supprimees au
    // moment de l'enregistrement, pas au clic, pour qu'un abandon de formulaire
    // ne detruise rien.
    removed: []
  }),
  computed: {
    dayTaken () {
      return !!this.conflict
    },
    dayTakenMessage () {
      if (!this.conflict) return ''
      return this.$t('intranet.homeContent.validation.dayTaken', {
        day: this.formatDay(this.day),
        quiz: this.conflict.label
      })
    },
    answersError () {
      if (this.answers.length < 2) return this.$t('intranet.homeContent.validation.twoAnswers')
      if (this.correctIndex === null) return this.$t('intranet.homeContent.validation.oneCorrect')
      if (this.answers.some(answer => !answer.label.fr || !answer.label.en)) {
        return this.$t('intranet.homeContent.validation.answerTranslations')
      }
      return null
    }
  },
  watch: {
    // Controle des que le jour change : decouvrir le conflit apres avoir redige
    // la question et les reponses serait cruel.
    day () {
      this.checkDay()
    }
  },
  created () {
    // L'API rend un horodatage complet ; le selecteur veut « AAAA-MM-JJ ».
    this.day = this.quiz.day ? this.quiz.day.slice(0, 10) : null

    // Sur une creation, les traductions arrivent absentes : sans ces gardes le
    // v-model ecrirait dans un undefined.
    for (const field of ['theme', 'question', 'explanation']) {
      if (!this.quiz[field]) this.$set(this.quiz, field, {})
    }

    this.answers = (this.quiz.answers || [])
      .slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map(answer => ({ ...answer, label: { ...answer.label } }))

    const correct = this.answers.findIndex(answer => answer.correct)
    this.correctIndex = correct === -1 ? null : correct

    // Deux rangees vides sur un quizz neuf : une question a une seule reponse
    // n'a pas de sens, autant les proposer d'emblee.
    if (!this.answers.length) {
      this.addAnswer()
      this.addAnswer()
    }

    this.dataLoaded = true
  },
  methods: {
    /** « lundi 5 octobre 2026 » a partir de « 2026-10-05 », sans fuseau. */
    formatDay (day) {
      if (!day) return ''
      const [year, month, date] = day.split('-').map(Number)
      return new Date(year, month - 1, date, 12)
        .toLocaleDateString(this.$i18n.locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    },

    /**
     * Cherche un quiz deja planifie le jour choisi.
     *
     * Une COMMODITE, pas la garantie : la garantie est l'index unique partiel
     * `quizzes_day_unique`, et CRUD-Wine refuse un jour pris. `submit`
     * rattrape la course perdue entre cette lecture et l'ecriture.
     */
    async checkDay () {
      this.conflict = null
      if (!this.day) return

      this.checkingDay = true
      try {
        const response = await Quiz.api().get(
          `quizzes?lang=${this.$i18n.locale}`,
          {
            baseURL: this.APIbaseURL,
            save: false,
            params: { filter: `day==${this.day}T00:00:00Z`, 'page[size]': 2 }
          }
        )
        // `response.response.data` est deja normalise par le
        // `transformResponse` du store : un tableau de quizz a plat, pas le
        // document JSON:API. Lire `.data` dessus ne trouvait jamais rien.
        const found = (response.response.data || [])
          // En edition, se croiser soi-meme n'est pas un conflit.
          .filter(entry => entry.id !== this.quiz.id)
        if (found.length) {
          const question = found[0].question || {}
          this.conflict = {
            id: found[0].id,
            label: question[this.$i18n.locale] || question.en || question.fr || '—'
          }
        }
      } catch (errors) {
        // Un echec de ce controle ne bloque pas la saisie : CRUD-Wine refusera
        // de toute facon un jour deja pris.
        this.conflict = null
      } finally {
        this.checkingDay = false
      }
    },

    /** Le refus de CRUD-Wine quand le jour est pris (champ `day`, `unique`). */
    isDayTaken (errors) {
      const body = errors && errors.response && errors.response.data
      if (!body || !Array.isArray(body.errors)) return false
      return body.errors.some(e => /day.*unique|already scheduled|quizzes_day_unique/.test(e.detail || ''))
    },

    addAnswer () {
      this.answers.push({ id: null, label: {}, correct: false, position: null })
    },

    removeAnswer (index) {
      const [answer] = this.answers.splice(index, 1)
      if (answer.id) this.removed.push(answer)

      // L'index de la bonne reponse designe une position dans le tableau : il
      // faut le suivre quand le tableau se decale, sinon retirer une rangee
      // au-dessus deplacerait silencieusement la bonne reponse.
      if (this.correctIndex === index) this.correctIndex = null
      else if (this.correctIndex > index) this.correctIndex -= 1
    },

    async submit () {
      // Dernier controle avant d'ecrire : le jour a pu etre pris depuis
      // l'ouverture du formulaire.
      await this.checkDay()
      if (this.dayTaken) {
        this.$toastr.e(this.dayTakenMessage)
        return
      }

      try {
        this.dataLoaded = false
        this.quiz.day = `${this.day}T00:00:00Z`

        const wasNew = this.quiz.$isNew
        const wanted = this.quiz.published

        const url = wasNew
          ? `quizzes?lang=${this.$i18n.locale}`
          : `quizzes/${this.quiz.id}?lang=${this.$i18n.locale}`

        const response = wasNew
          ? await Quiz.api().post(url, this.quiz, { baseURL: this.APIbaseURL })
          : await Quiz.api().patch(url, this.quiz, { baseURL: this.APIbaseURL })

        const quizId = wasNew ? response.entities.quizzes[0].id : this.quiz.id

        await this.saveAnswers(quizId)

        // Publication en second geste, et seulement a la creation : CRUD-Wine
        // force `published = false` a la creation parce que les reponses
        // n'existent pas encore. Sans ce rattrapage, un quizz cree « publie »
        // resterait un brouillon en silence.
        if (wasNew && wanted) {
          await Quiz.api().patch(
            `quizzes/${quizId}`,
            { $entity_name: Quiz.entity, id: quizId, published: true },
            { baseURL: this.APIbaseURL, save: false }
          )
        }

        this.$toastr.s(this.$t('intranet.referential.title.saveConfirmation'))
        this.$router.push('/authenticated/quizzes/list')
      } catch (errors) {
        this.dataLoaded = true

        // Course perdue : le jour a ete pris entre notre controle et notre
        // ecriture. On le dit en clair plutot que de laisser passer l'erreur.
        if (this.isDayTaken(errors)) {
          await this.checkDay()
          this.$toastr.e(this.dayTakenMessage || this.$t('intranet.homeContent.validation.dayTakenRace'))
          return
        }

        errorHandler(errors, this)
      }
    },

    /**
     * Aligne les reponses sur ce qu'affiche le formulaire.
     *
     * Sequentiel et non en parallele : la base n'accepte qu'une bonne reponse a
     * la fois. Deplacer la bonne reponse de A vers B en deux requetes
     * simultanees ferait echouer celle de B si celle de A n'est pas encore
     * passee. On retire donc d'abord, on repose ensuite.
     */
    async saveAnswers (quizId) {
      for (const answer of this.removed) {
        await QuizAnswer.api().delete(`quiz-answers/${answer.id}`, { baseURL: this.APIbaseURL, save: false })
      }
      this.removed = []

      // Toute charge utile partielle porte DEUX champs de service :
      //
      //   `id`            le serialiseur de la toolbox fait
      //                   `serializedData.data.id.charAt()` sans garde sur la
      //                   branche « corps simple ». Sans id, la requete leve
      //                   avant meme de partir.
      //   `$entity_name`  c'est de la que le dataTree tire le type JSON:API.
      //                   Sans lui le corps part avec `"type": null`, et
      //                   `JAPI::Resource#type` n'est pas nilable cote creddy :
      //                   400 « Bad HTTP Body ».
      //
      // On lit le nom sur le modele plutot que d'ecrire le litteral, pour que
      // les deux ne puissent pas diverger.

      // Premiere passe : tout le monde a false, pour liberer l'index unique.
      for (const answer of this.answers) {
        if (answer.id && answer.correct) {
          await QuizAnswer.api().patch(
            `quiz-answers/${answer.id}`,
            { $entity_name: QuizAnswer.entity, id: answer.id, correct: false },
            { baseURL: this.APIbaseURL, save: false }
          )
        }
      }

      // Seconde passe : positions, libelles, et la bonne reponse retablie.
      for (const [index, answer] of this.answers.entries()) {
        const payload = {
          label: answer.label,
          correct: index === this.correctIndex,
          position: index + 1
        }

        if (answer.id) {
          await QuizAnswer.api().patch(
            `quiz-answers/${answer.id}`,
            { $entity_name: QuizAnswer.entity, id: answer.id, ...payload },
            { baseURL: this.APIbaseURL, save: false }
          )
        } else {
          const draft = await QuizAnswer.new()
          Object.assign(draft, payload)
          draft.quiz_id = quizId
          // `$entity_name` est indispensable : le dataTree en tire le type
          // JSON:API du lien. Un objet nu donnerait `{"type": null}`, et
          // `JAPI::ResourceLinkage#type` n'est pas nilable cote creddy.
          draft.quiz = { $entity_name: 'quizzes', id: quizId }
          draft.$isNew = true
          await QuizAnswer.api().post('quiz-answers', draft, { baseURL: this.APIbaseURL, save: false })
        }
      }
    }
  }
}
</script>
